import React, { useEffect, useState, useCallback } from "react";
import { useNotify } from "react-admin";
import { Box, Button, Card, CardContent, CircularProgress, Collapse, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { bucketTotalsOf, invalidReasonFor } from "../methodologyUtils";
import { StateBadge, ImpactText } from "../badges";
import { fieldImpactLabel, isFieldRequired } from "../labels";
import FieldDrillPanel from "../panels/FieldDrillPanel";

// PENDIENTES (spec §5): the actual "Acciones" screen turned into a decision-oriented worklist.
// Answers "¿qué necesita intervención y qué puedo hacer?". Items are grouped by the DECISION
// they need, filterable by the §5 categories. Each item follows the spec hierarchy:
// displayName → contexto → estado humano → impacto → acción. fieldKey/IDs are secondary.
//
// Data: reuses the shared gapFields (Necesitan datos / Inválidos) so gaps aren't refetched
// (§10); fetches only the category-specific /assumptions/pending and /translations locally.

const useStyles = makeStyles((theme) => ({
  head: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: theme.spacing(1) },
  title: { fontSize: 18, fontWeight: 700 },
  filters: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", marginBottom: theme.spacing(2) },
  chip: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: "4px 14px",
    fontSize: 13,
    cursor: "pointer",
    background: theme.palette.background.paper,
    font: "inherit",
  },
  chipActive: { background: theme.palette.primary.main, color: "#fff", borderColor: theme.palette.primary.main },
  groupTitle: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.palette.text.secondary, margin: theme.spacing(2, 0, 1) },
  item: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(1),
  },
  itemMain: { flex: "1 1 auto", minWidth: 0 },
  name: { fontSize: 15, fontWeight: 600 },
  context: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  proposal: { fontSize: 13, marginTop: 4 },
  badges: { display: "flex", gap: theme.spacing(1), alignItems: "center", flexWrap: "wrap", marginTop: 6 },
  secondary: { fontSize: 11, color: "#94a3b8", marginTop: 4 },
  actions: { display: "flex", gap: theme.spacing(1), alignItems: "center", flexShrink: 0 },
  empty: { color: theme.palette.text.secondary, padding: theme.spacing(2, 0) },
  val: { color: "#059669", fontWeight: 600, fontSize: 13 },
}));

const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "asumibles", label: "Puedes asumir" },
  { key: "faltan", label: "Faltan datos" },
  { key: "revisar", label: "Revisar" },
  { key: "invalidos", label: "Inválidos" },
];

const PendientesScreen = ({ api, projectId, contracted, data, initialFilter }) => {
  const classes = useStyles();
  const notify = useNotify();

  const [filter, setFilter] = useState(initialFilter || "todos");
  const [asumibles, setAsumibles] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [busy, setBusy] = useState(false); // global (assume-all)
  const [rowBusy, setRowBusy] = useState(null); // id of the row being mutated (anti double-submit)
  const [drill, setDrill] = useState(null);
  const [showOptional, setShowOptional] = useState(false);

  // Follow deep-links from Resumen when the incoming filter changes.
  useEffect(() => {
    if (initialFilter) setFilter(initialFilter);
  }, [initialFilter]);

  const { gapFields, reload: reloadShared } = data;

  const loadLocal = useCallback(async () => {
    if (!projectId) return;
    setLocalLoading(true);
    const [pend, trans] = await Promise.all([
      api("GET", `/projects/${projectId}/assumptions/pending`),
      api("GET", `/projects/${projectId}/translations`),
    ]);
    setLocalLoading(false);
    setAsumibles(pend.ok ? pend.data.assumptions ?? [] : []);
    setTranslations(trans.ok && Array.isArray(trans.data) ? trans.data : []);
  }, [api, projectId]);

  useEffect(() => {
    loadLocal();
  }, [loadLocal]);

  // Refresh both the local category data and the shared dataset (so the tab badge, Resumen
  // and Datos stay in sync) after any mutation.
  const reloadAll = useCallback(() => {
    loadLocal();
    if (reloadShared) reloadShared();
  }, [loadLocal, reloadShared]);

  // Derived categories from the shared field list.
  const byId = {};
  (gapFields || []).forEach((f) => (byId[f.fieldDefinitionId] = f));
  const asumIds = new Set(asumibles.map((a) => a.fieldDefinitionId));

  // A gap matters (main worklist) only if its field is REQUIRED (M/M-A) for a contracted
  // methodology. Recommended-only (R) fields go to the "Opcional" section so they do not
  // overwhelm the primary flow (see coherence review).
  const isReq = (f) => isFieldRequired(f, contracted);

  const asumReq = asumibles.filter((a) => isReq(byId[a.fieldDefinitionId] || a));
  const asumOpt = asumibles.filter((a) => !isReq(byId[a.fieldDefinitionId] || a));

  const pedirAll = (gapFields || [])
    .map((f) => ({ ...f, faltan: bucketTotalsOf(f.statusCounts).faltan }))
    .filter((f) => f.faltan > 0 && !asumIds.has(f.fieldDefinitionId))
    .sort((a, b) => b.faltan - a.faltan);
  const pedir = pedirAll.filter(isReq);
  const pedirOpt = pedirAll.filter((f) => !isReq(f));

  const invalidosAll = (gapFields || [])
    .map((f) => ({ ...f, revisar: bucketTotalsOf(f.statusCounts).revisar }))
    .filter((f) => f.revisar > 0)
    .sort((a, b) => b.revisar - a.revisar);
  const invalidos = invalidosAll.filter(isReq);
  const invalidosOpt = invalidosAll.filter((f) => !isReq(f));

  const total =
    asumReq.reduce((n, a) => n + (a.pendingCount || 0), 0) +
    pedir.reduce((n, f) => n + f.faltan, 0) +
    invalidos.reduce((n, f) => n + f.revisar, 0) +
    translations.length;

  const optionalCount =
    asumOpt.reduce((n, a) => n + (a.pendingCount || 0), 0) +
    pedirOpt.reduce((n, f) => n + f.faltan, 0) +
    invalidosOpt.reduce((n, f) => n + f.revisar, 0);
  const optionalItems = [
    ...asumOpt.map((a) => ({ kind: "asum", f: a })),
    ...pedirOpt.map((f) => ({ kind: "pedir", f })),
    ...invalidosOpt.map((f) => ({ kind: "inval", f })),
  ];

  // ── Mutations (same contracts as the legacy AccionesPanel) ──
  const assumeField = async (fd) => {
    const r = await api("PATCH", `/gaps/bulk`, {
      fieldDefinitionId: fd.fieldDefinitionId,
      projectId,
      action: "RESOLVED_ASSUMPTION",
      resolvedByActor: "BCOME",
      statusIn: ["ASSUMPTION_PENDING"],
    });
    if (!r.ok) return { ok: false, processed: 0, errors: [{ message: `HTTP ${r.status}` }] };
    return { ok: true, processed: r.data?.processed ?? 0, errors: r.data?.errors ?? [] };
  };

  const assumeOne = async (fd) => {
    setRowBusy(fd.fieldDefinitionId);
    const res = await assumeField(fd);
    setRowBusy(null);
    if (!res.ok) notify("Error al asumir", "error");
    else if (res.processed > 0) notify(`Asumido: ${fd.displayName} (${res.processed})`, "success");
    else notify(`No se pudo asumir "${fd.displayName}": ${res.errors[0]?.message || "sin default aplicable"}`, "warning");
    reloadAll();
  };

  const assumeAll = async () => {
    if (!window.confirm(`¿Asumir los ${asumReq.length} campos con valor por defecto de Bcome?`)) return;
    setBusy(true);
    let processed = 0;
    let failed = 0;
    for (const fd of asumReq) {
      const res = await assumeField(fd);
      if (res.processed > 0) processed += res.processed;
      else failed++;
    }
    setBusy(false);
    notify(
      `Asumidos ${processed} hueco${processed !== 1 ? "s" : ""}${failed ? ` · ${failed} campo(s) sin default aplicable` : ""}`,
      processed ? "success" : "warning"
    );
    reloadAll();
  };

  const confirmTranslation = async (t) => {
    if (!t.resolvedEntityId) return notify("Sin sugerencia de catálogo — no se puede confirmar", "warning");
    setRowBusy(t.id);
    const r = await api("PATCH", `/translations/${t.id}`, { confirmedValue: t.rawValue, targetEntityId: t.resolvedEntityId });
    setRowBusy(null);
    if (r.ok) notify(`Confirmado: "${t.rawValue}" → ${t.resolvedValue || "catálogo"}`, "success");
    else notify(`Error al confirmar: ${r.data?.message || `HTTP ${r.status}`}`, "error");
    reloadAll();
  };

  const rejectTranslation = async (t) => {
    setRowBusy(t.id);
    const r = await api("PATCH", `/translations/${t.id}/reject`);
    setRowBusy(null);
    if (r.ok) notify(`Descartado: "${t.rawValue}"`, "info");
    else notify(`Error al rechazar: ${r.data?.message || `HTTP ${r.status}`}`, "error");
    reloadAll();
  };

  const show = (key) => filter === "todos" || filter === key;
  const openDrill = (f) => setDrill({ fieldKey: f.fieldKey, displayName: f.displayName });

  // Transparency: before applying, show WHICH value will be used. Static assumption → the exact
  // value; dynamic → the rule (resolved per entity at apply time), so we don't one-click a black box.
  const hasStaticValue = (f) => !f.isDynamic && f.assumptionValue != null && f.assumptionValue !== "";
  const assumptionProposal = (f) =>
    hasStaticValue(f) ? (
      <span>
        Valor propuesto: <strong>{String(f.assumptionValue)}</strong>
        {f.confidence ? <span style={{ color: "#94a3b8" }}> · confianza {String(f.confidence).toLowerCase()}</span> : null}
      </span>
    ) : (
      <span style={{ color: "#64748b" }}>Se calcula por entidad{f.label ? `: ${f.label}` : ""}</span>
    );
  const assumptionAction = (f) =>
    hasStaticValue(f) ? (
      <Button size="small" variant="outlined" color="primary" disabled={rowBusy === f.fieldDefinitionId} onClick={() => assumeOne(f)}>
        {rowBusy === f.fieldDefinitionId ? <CircularProgress size={16} /> : "Usar este valor"}
      </Button>
    ) : (
      <Button size="small" onClick={() => openDrill(f)}>
        Revisar assumption
      </Button>
    );

  if (localLoading && !asumibles.length && !translations.length && !(gapFields || []).length) {
    return (
      <Card>
        <CardContent>
          <Box textAlign="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const nothing =
    !asumReq.length && !pedir.length && !invalidos.length && !translations.length && !optionalItems.length;

  return (
    <Card>
      <CardContent>
        <div className={classes.head}>
          <span className={classes.title}>Pendientes</span>
          <Typography variant="body2" color="textSecondary">
            <strong>{total}</strong> valor{total === 1 ? "" : "es"} requiere{total === 1 ? "" : "n"} atención
            {optionalCount > 0 && (
              <span style={{ color: "#94a3b8" }}> · {optionalCount} recomendado{optionalCount === 1 ? "" : "s"}</span>
            )}
          </Typography>
        </div>

        <div className={classes.filters}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${classes.chip} ${filter === f.key ? classes.chipActive : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {nothing ? (
          <Typography className={classes.empty}>No hay pendientes. El proyecto está al día.</Typography>
        ) : (
          <>
            {/* ── Puedes asumir ── */}
            {show("asumibles") && (
              <>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <div className={classes.groupTitle}>Puedes asumir · {asumReq.length} tipo{asumReq.length === 1 ? "" : "s"} de dato</div>
                  {asumReq.length > 0 && (
                    <Button size="small" variant="contained" color="primary" disabled={busy} onClick={assumeAll}>
                      {busy ? <CircularProgress size={16} color="inherit" /> : `Asumir todos (${asumReq.length})`}
                    </Button>
                  )}
                </Box>
                {asumReq.length ? (
                  asumReq.map((f) => (
                    <PendItem
                      key={f.fieldDefinitionId}
                      classes={classes}
                      name={f.displayName}
                      fieldKey={f.fieldKey}
                      context={`${f.pendingCount} hueco${f.pendingCount !== 1 ? "s" : ""}`}
                      proposal={assumptionProposal(f)}
                      status="ASSUMPTION_PENDING"
                      impact={fieldImpactLabel(byId[f.fieldDefinitionId] || f, { allowed: contracted })}
                      action={assumptionAction(f)}
                    />
                  ))
                ) : (
                  <Typography className={classes.empty}>Nada asumible.</Typography>
                )}
              </>
            )}

            {/* ── Necesitan datos ── */}
            {show("faltan") && (
              <>
                <div className={classes.groupTitle}>Faltan datos · {pedir.length} tipo{pedir.length === 1 ? "" : "s"} de dato</div>
                {pedir.length ? (
                  pedir.map((f) => (
                    <PendItem
                      key={f.fieldDefinitionId}
                      classes={classes}
                      name={f.displayName}
                      fieldKey={f.fieldKey}
                      context={`${f.faltan} hueco${f.faltan !== 1 ? "s" : ""} · ${f.articleCount} artículo${f.articleCount === 1 ? "" : "s"}`}
                      status="MISSING"
                      impact={fieldImpactLabel(f, { allowed: contracted })}
                      action={
                        <Button size="small" onClick={() => openDrill(f)}>
                          Ver {f.articleCount} artículo{f.articleCount === 1 ? "" : "s"}
                        </Button>
                      }
                    />
                  ))
                ) : (
                  <Typography className={classes.empty}>Nada pendiente de datos.</Typography>
                )}
              </>
            )}

            {/* ── Revisar (traducciones) ── */}
            {show("revisar") && (
              <>
                <div className={classes.groupTitle}>Revisar · {translations.length}</div>
                {translations.length ? (
                  translations.map((t) => (
                    <div key={t.id} className={classes.item}>
                      <div className={classes.itemMain}>
                        <div className={classes.name}>{t.sourceField || "Valor sin traducir"}</div>
                        <div className={classes.context}>
                          Valor recibido: <strong>{t.rawValue}</strong>
                        </div>
                        <div className={classes.context} style={{ marginTop: 4 }}>
                          {t.resolvedValue
                            ? `Sugerencia: ${t.resolvedValue}${t.confidence ? ` (${t.confidence})` : ""}`
                            : "No hemos podido relacionarlo con una categoría conocida. Sugerencia: —"}
                        </div>
                        <div className={classes.secondary}>
                          {t.catalogTable ? `${t.catalogTable}` : ""}
                          {t.affectedEntities?.length ? ` · afecta a ${t.affectedEntities.length} entidad(es)` : ""}
                        </div>
                      </div>
                      <div className={classes.actions}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          disabled={rowBusy === t.id || !t.resolvedEntityId}
                          onClick={() => confirmTranslation(t)}
                        >
                          {rowBusy === t.id ? <CircularProgress size={16} /> : "Confirmar"}
                        </Button>
                        <Button size="small" variant="outlined" disabled={rowBusy === t.id} onClick={() => rejectTranslation(t)}>
                          Rechazar
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <Typography className={classes.empty}>Nada que revisar.</Typography>
                )}
              </>
            )}

            {/* ── Inválidos (reglas de negocio) ── */}
            {show("invalidos") && (
              <>
                <div className={classes.groupTitle}>Inválidos · {invalidos.length} tipo{invalidos.length === 1 ? "" : "s"} de dato</div>
                {invalidos.length ? (
                  invalidos.map((f) => (
                    <PendItem
                      key={f.fieldDefinitionId}
                      classes={classes}
                      name={f.displayName}
                      fieldKey={f.fieldKey}
                      context={invalidReasonFor(f.fieldKey)}
                      status="INVALID_VALUE"
                      impact={fieldImpactLabel(f, { allowed: contracted })}
                      secondary={`${f.revisar} inválido${f.revisar !== 1 ? "s" : ""} · ${f.articleCount} artículo${f.articleCount === 1 ? "" : "s"}`}
                      action={
                        <Button size="small" onClick={() => openDrill(f)}>
                          Ver {f.articleCount} artículo{f.articleCount === 1 ? "" : "s"}
                        </Button>
                      }
                    />
                  ))
                ) : (
                  <Typography className={classes.empty}>Nada inválido.</Typography>
                )}
              </>
            )}

            {/* ── Recomendados — opcionales, colapsados por defecto (reducen la carga cognitiva). ── */}
            {optionalItems.length > 0 && (
              <div style={{ marginTop: 16, borderTop: "1px dashed #e2e8f0", paddingTop: 12 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>Recomendados · {optionalCount}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>No impiden completar las metodologías actuales.</div>
                  </div>
                  <Button size="small" onClick={() => setShowOptional((v) => !v)}>
                    {showOptional ? "Ocultar recomendados" : "Mostrar recomendados"}
                  </Button>
                </Box>
                <Collapse in={showOptional}>
                  <div style={{ marginTop: 10 }}>
                    {optionalItems.map(({ kind, f }) =>
                      kind === "asum" ? (
                        <PendItem
                          key={`opt-asum-${f.fieldDefinitionId}`}
                          classes={classes}
                          name={f.displayName}
                          fieldKey={f.fieldKey}
                          context={`${f.pendingCount} hueco${f.pendingCount !== 1 ? "s" : ""}`}
                          proposal={assumptionProposal(f)}
                          status="ASSUMPTION_PENDING"
                          impact=""
                          secondary="Recomendado"
                          action={assumptionAction(f)}
                        />
                      ) : (
                        <PendItem
                          key={`opt-${kind}-${f.fieldDefinitionId}`}
                          classes={classes}
                          name={f.displayName}
                          fieldKey={f.fieldKey}
                          context={kind === "inval" ? invalidReasonFor(f.fieldKey) : `${f.faltan} hueco${f.faltan !== 1 ? "s" : ""} · ${f.articleCount} artículo${f.articleCount === 1 ? "" : "s"}`}
                          status={kind === "inval" ? "INVALID_VALUE" : "MISSING"}
                          impact=""
                          secondary="Recomendado"
                          action={
                            <Button size="small" onClick={() => openDrill(f)}>
                              Ver {f.articleCount} artículo{f.articleCount === 1 ? "" : "s"}
                            </Button>
                          }
                        />
                      )
                    )}
                  </div>
                </Collapse>
              </div>
            )}
          </>
        )}

        {drill && (
          <FieldDrillPanel
            api={api}
            projectId={projectId}
            field={drill}
            contracted={contracted}
            onClose={() => {
              setDrill(null);
              reloadAll();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

// One worklist item following the spec §5 hierarchy: name → context → state + impact → action.
// fieldKey (the raw internal key) is NOT shown on the row — it lives as a tooltip / technical
// detail, so the primary line stays human-readable.
const PendItem = ({ classes, name, fieldKey, context, proposal, status, impact, secondary, extra, action }) => (
  <div className={classes.item}>
    <div className={classes.itemMain}>
      <div className={classes.name} title={fieldKey || undefined}>{name}</div>
      {context && <div className={classes.context}>{context}</div>}
      {proposal && <div className={classes.proposal}>{proposal}</div>}
      <div className={classes.badges}>
        <StateBadge status={status} />
        {impact ? <ImpactText label={impact} blocking={status !== "ASSUMPTION_PENDING"} /> : null}
        {extra}
      </div>
      {secondary && <div className={classes.secondary}>{secondary}</div>}
    </div>
    {action && <div className={classes.actions}>{action}</div>}
  </div>
);

export default PendientesScreen;
